/**
 * 顧客管理画面
 *
 * 管理者用：顧客の一覧・追加・編集・削除、FAX転送先アドレスの表示
 */

import { useState, useEffect, useCallback } from "react";
import {
  Title,
  Stack,
  Group,
  Alert,
  Flex,
  Box,
} from "@mantine/core";
import { showSuccess, showError } from "../utils/notifications";
import {
  Customer,
  fetchCustomers,
  createCustomer,
  updateCustomer,
  deleteCustomer,
} from "../services/customerService";
import { Users } from "lucide-react";

import { CustomerListPanel } from "../components/customer/CustomerListPanel";
import { CustomerDetailWrapper } from "../components/customer/CustomerDetailWrapper";
import {
  AddCustomerModal,
  EditCustomerModal,
  DeleteConfirmModal,
} from "../components/customer/CustomerModals";

export function CustomersPage() {
  const [customers, setCustomers] = useState<Customer[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedCustomerId, setSelectedCustomerId] = useState<string | null>(
    null
  );

  const selectedCustomer =
    customers.find((c) => c.customerId === selectedCustomerId) || null;

  // 追加モーダル
  const [addModalOpen, setAddModalOpen] = useState(false);
  const [newCustomerId, setNewCustomerId] = useState("");
  const [newCustomerName, setNewCustomerName] = useState("");
  const [newNotes, setNewNotes] = useState("");
  const [adding, setAdding] = useState(false);

  // 詳細/編集モーダル
  const [editTarget, setEditTarget] = useState<Customer | null>(null);
  const [editName, setEditName] = useState("");
  const [editNotes, setEditNotes] = useState("");
  const [editStatus, setEditStatus] = useState<"active" | "inactive">("active");
  const [saving, setSaving] = useState(false);

  // 削除確認モーダル
  const [deleteTarget, setDeleteTarget] = useState<Customer | null>(null);
  const [deleting, setDeleting] = useState(false);

  // 顧客一覧を取得
  const loadCustomers = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await fetchCustomers();
      setCustomers(data);
    } catch (err) {
      setError(
        err instanceof Error ? err.message : "顧客一覧の取得に失敗しました"
      );
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadCustomers();
  }, [loadCustomers]);

  // 顧客追加
  const handleAdd = async () => {
    if (!newCustomerId || !newCustomerName) return;

    try {
      setAdding(true);
      await createCustomer(newCustomerId, newCustomerName, newNotes);
      showSuccess(`${newCustomerName} を追加しました`);
      setAddModalOpen(false);
      setNewCustomerId("");
      setNewCustomerName("");
      setNewNotes("");
      await loadCustomers();
    } catch (err) {
      showError(err instanceof Error ? err.message : "顧客の追加に失敗しました");
    } finally {
      setAdding(false);
    }
  };

  // 顧客編集モーダルを開く
  const openEditModal = (e: React.MouseEvent, customer: Customer) => {
    e.stopPropagation(); // リスト選択の発火を防ぐ
    setEditTarget(customer);
    setEditName(customer.customerName);
    setEditNotes(customer.notes || "");
    setEditStatus(customer.status);
  };

  // 顧客更新（名前、備考、ステータスを一括保存）
  const handleUpdate = async () => {
    if (!editTarget) return;

    try {
      setSaving(true);
      await updateCustomer(editTarget.customerId, {
        customerName: editName,
        notes: editNotes,
        status: editStatus,
      });
      showSuccess("顧客情報を更新しました");
      setEditTarget(null);
      await loadCustomers();
    } catch (err) {
      showError(err instanceof Error ? err.message : "顧客の更新に失敗しました");
    } finally {
      setSaving(false);
    }
  };

  // 顧客削除
  const handleDelete = async () => {
    if (!deleteTarget) return;

    try {
      setDeleting(true);
      await deleteCustomer(deleteTarget.customerId);
      showSuccess(`${deleteTarget.customerName} を削除しました`);
      setDeleteTarget(null);
      setEditTarget(null);
      await loadCustomers();
    } catch (err) {
      showError(err instanceof Error ? err.message : "顧客の削除に失敗しました");
    } finally {
      setDeleting(false);
    }
  };

  return (
    <Box
      style={{
        height:
          "calc(100vh - var(--app-shell-header-offset) - var(--app-shell-padding) * 2)",
        overflow: "hidden",
      }}
    >
      {/* AIアシスタント向けガイド */}
      <div data-ai-guide hidden>
        {`[ページ: 顧客管理]
[概要: 顧客の一覧表示・追加・編集・削除ができる画面]
[構成: 左=顧客リスト, 右=選択した顧客の詳細パネル]
[登録顧客数: ${customers.length}件]
[選択中: ${selectedCustomer ? `${selectedCustomer.customerName} (ID: ${selectedCustomer.customerId})` : "なし"}]
[ダイアログ]
- 追加ダイアログ: ${addModalOpen ? "表示中" : "非表示"} (新規顧客登録用)
  - 入力項目: 顧客ID(必須・変更不可), 顧客名(必須), 備考(任意)
  - 顧客IDを入力するとFAX転送先アドレスが自動生成される
- 編集ダイアログ: ${editTarget ? "表示中" : "非表示"} (顧客情報の変更用)
- 削除確認ダイアログ: ${deleteTarget ? "表示中" : "非表示"}
[操作のヒント]
- 顧客を削除するには、先に無効化（ステータスをOFF）にする必要がある
- 顧客IDは登録後に変更できないので注意
- FAX転送先アドレスは顧客のFAX機に設定するメールアドレス`}
      </div>

      <Stack gap="sm" h="100%">
        <Group justify="space-between">
          <Group>
            <Users size={28} />
            <Title order={2}>顧客管理</Title>
          </Group>
        </Group>

        {error && (
          <Alert color="red" title="エラー">
            {error}
          </Alert>
        )}

        <Flex gap="md" h="100%" align="stretch" style={{ overflow: "hidden" }}>
          {/* 左サイドバー: 顧客一覧 */}
          <Box
            style={{
              width: 420,
              minWidth: 340,
              height: "100%",
              flexShrink: 0.3,
              display: "flex",
              flexDirection: "column",
            }}
          >
            <CustomerListPanel
              customers={customers}
              loading={loading}
              selectedCustomerId={selectedCustomerId}
              onSelect={setSelectedCustomerId}
              onEdit={openEditModal}
              onAddClick={() => setAddModalOpen(true)}
            />
          </Box>

          {/* 右パネル: 詳細・設定 */}
          <Box
            style={{
              flex: 1,
              height: "100%",
              minWidth: 300,
              flexShrink: 1,
              display: "flex",
              flexDirection: "column",
            }}
          >
            <CustomerDetailWrapper customer={selectedCustomer} />
          </Box>
        </Flex>
      </Stack>

      {/* 追加モーダル */}
      <AddCustomerModal
        opened={addModalOpen}
        onClose={() => setAddModalOpen(false)}
        customerId={newCustomerId}
        setCustomerId={setNewCustomerId}
        customerName={newCustomerName}
        setCustomerName={setNewCustomerName}
        notes={newNotes}
        setNotes={setNewNotes}
        onAdd={handleAdd}
        adding={adding}
      />

      {/* 編集モーダル */}
      <EditCustomerModal
        customer={editTarget}
        onClose={() => setEditTarget(null)}
        editName={editName}
        setEditName={setEditName}
        editNotes={editNotes}
        setEditNotes={setEditNotes}
        editStatus={editStatus}
        setEditStatus={setEditStatus}
        onUpdate={handleUpdate}
        saving={saving}
        onDelete={() => setDeleteTarget(editTarget)}
      />

      {/* 削除確認モーダル */}
      <DeleteConfirmModal
        customer={deleteTarget}
        onClose={() => setDeleteTarget(null)}
        onDelete={handleDelete}
        deleting={deleting}
      />
    </Box>
  );
}
